import { useEffect, useMemo, useState } from 'react'
import { Award, Lock, Save, Send, RotateCcw, ArrowLeft } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import KempenvisionNav from '@/components/KempenvisionNav'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select'
import { visionAPI } from '../lib/api'
import { useVisionSocket } from '../lib/visionSocket'
import TeamMark, { parseFlag } from '@/components/TeamMark'

const NO_POINTS = '__none__'



export default function KempenvisionJuryPage() {
  const [data, setData]     = useState(null) // { show, jury, entries, votes, available_points }
  const [votes, setVotes]   = useState({})   // entry_id -> points|null
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError]   = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const load = async () => {
    setLoading(true)
    try {
      const { data } = await visionAPI.getMyJury()
      setData(data)
      const v = {}
      data.votes?.forEach(x => { v[x.entry_id] = Number(x.points) })
      setVotes(v)
    } catch (e) { setError(e.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])
  useVisionSocket((p) => { if (p.scope === 'show' || p.scope === 'jury') load() })

  if (loading) return <div className="p-6 text-muted-foreground">Laden…</div>
  if (!data) return <div className="p-6 text-red-500">Kon jury-info niet laden.</div>
  if (!data.show) {
    return (
      <div className="max-w-2xl mx-auto">
        <KempenvisionNav />
        <Card><CardContent className="p-6 text-center text-muted-foreground">
          Geen actieve Kempenvision show. Vraag een admin om er één te activeren.
        </CardContent></Card>
      </div>
    )
  }
  if (!data.jury) {
    return (
      <div className="max-w-2xl mx-auto">
        <KempenvisionNav />
        <Card><CardContent className="p-6 text-center text-muted-foreground">
          Je bent niet gekoppeld aan een jury voor <b>{data.show.name}</b>. Vraag een admin om je aan een jury te koppelen.
          <div className="mt-4"><Button asChild variant="outline"><Link to="/kempenvision">← Terug</Link></Button></div>
        </CardContent></Card>
      </div>
    )
  }

  const { show, jury, entries, available_points } = data
  const locked = jury.is_locked

  const usedPoints = new Set(
    Object.entries(votes).filter(([, p]) => p != null).map(([, p]) => p)
  )
  const pointsAssigned = usedPoints.size
  const totalPoints = available_points.length

  const changePoints = (entryId, val) => {
    if (locked) return
    setError(null); setSavedAt(null)
    setVotes(prev => {
      const next = { ...prev }
      if (val === NO_POINTS || val === '' || val == null) delete next[entryId]
      else next[entryId] = Number(val)
      return next
    })
  }

  const buildPayload = () =>
    Object.entries(votes)
      .filter(([, p]) => p != null)
      .map(([entry_id, points]) => ({ entry_id: Number(entry_id), points }))

  const save = async () => {
    setSaving(true); setError(null)
    try {
      await visionAPI.saveJuryVotes(buildPayload())
      setSavedAt(new Date())
    } catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setSaving(false) }
  }

  const submit = async () => {
    if (!confirm('Definitief indienen? Je stemmen worden gelockt en kunnen enkel door een admin heropend worden.')) return
    setSaving(true); setError(null)
    try {
      await visionAPI.saveJuryVotes(buildPayload())
      await visionAPI.submitJury()
      await load()
    } catch (e) { setError(e.response?.data?.error ?? e.message) } finally { setSaving(false) }
  }

  const reset = () => {
    if (!confirm('Alle punten wissen?')) return
    setVotes({}); setSavedAt(null)
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <KempenvisionNav />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-[4px] border border-border bg-card flex items-center justify-center" style={{ borderLeft: "2px solid rgb(var(--gold-rgb))" }}>
            <Award className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Jury stemming</div>
            <h1 className="text-2xl font-bold">{jury.name}</h1>
            <p className="text-sm text-muted-foreground">
              {show.name}{show.edition ? ` — ${show.edition}` : ''}
              {jury.spokesperson ? ` · Spokesperson: ${jury.spokesperson}` : ''}
            </p>
          </div>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/kempenvision"><ArrowLeft className="h-4 w-4 mr-1" />Overzicht</Link>
        </Button>
      </div>

      {locked && (
        <Card className="border-emerald-500/40 bg-emerald-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <Lock className="h-5 w-5 text-emerald-400" />
            <div className="flex-1">
              <div className="font-semibold text-emerald-400">Ingediend</div>
              <div className="text-sm text-muted-foreground">
                Je stemmen zijn gelockt. Vraag een admin om te unlocken als er iets moet veranderen.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Instructies</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Verdeel de punten over de entries. Elke waarde kan maar <b>één keer</b> worden gegeven.
            Je hoeft niet alle punten uit te delen als je maar met een aantal entries iets kan.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {available_points.slice().reverse().map(p => (
              <Badge key={p} variant={usedPoints.has(p) ? 'default' : 'outline'}
                     className={usedPoints.has(p) ? 'bg-yellow-500 text-black' : 'text-muted-foreground'}>
                {p}
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-3 pt-1">
            <div className="text-sm text-muted-foreground">
              {pointsAssigned}/{totalPoints} punt-waardes toegekend
            </div>
            <div className="flex-1" />
            {savedAt && !error && <span className="text-xs text-emerald-400">✓ Opgeslagen {savedAt.toLocaleTimeString()}</span>}
            {error && <span className="text-xs text-red-500">✕ {error}</span>}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {entries.map(e => {
          const flag = parseFlag(e.team_flag_colors)
          const current = votes[e.id] ?? null
          return (
            <Card key={e.id} className={current ? 'border-yellow-500/40' : ''}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="font-mono text-xs text-muted-foreground w-8 text-center shrink-0">#{e.running_order}</div>
                <TeamMark flag_colors={flag} logo_url={e.team_logo_url} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {e.team_name}{e.artist_label ? ` · ${e.artist_label}` : ''} · <span className="uppercase">{e.genre}</span>
                  </div>
                  {e.audio_url && <audio controls src={e.audio_url} className="mt-2 h-8 w-full max-w-md" preload="none" />}
                </div>
                <div className="shrink-0 w-40">
                  <Select
                    value={current == null ? NO_POINTS : String(current)}
                    onValueChange={(v) => changePoints(e.id, v)}
                    disabled={locked}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Punten…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_POINTS}>— geen —</SelectItem>
                      {available_points.slice().reverse().map(p => {
                        const disabled = usedPoints.has(p) && p !== current
                        return (
                          <SelectItem key={p} value={String(p)} disabled={disabled}>
                            {p} punten{disabled ? ' (bezet)' : ''}
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {entries.length === 0 && (
        <Card><CardContent className="p-6 text-center text-muted-foreground">
          Er zijn nog geen entries voor deze show. Wacht tot admin ze aanmaakt.
        </CardContent></Card>
      )}

      {!locked && entries.length > 0 && (
        <div className="sticky bottom-4 flex gap-2 justify-end bg-background/90 backdrop-blur p-3 rounded-lg border border-border shadow-lg">
          <Button variant="ghost" onClick={reset}><RotateCcw className="h-4 w-4 mr-1" />Wissen</Button>
          <Button variant="secondary" onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? 'Opslaan…' : 'Opslaan'}</Button>
          <Button onClick={submit} disabled={saving}><Send className="h-4 w-4 mr-1" />Definitief indienen</Button>
        </div>
      )}
    </div>
  )
}
